import {
    createContext,
    type PropsWithChildren,
    useContext,
    useMemo,
    useState,
    useEffect
} from "react";

import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import { CategoryOrder } from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order";
import { getTemplateData } from "@/utils/content-queries/get-content-data";
import { getCurrentRoute } from "@/utils/get-current-route";

import { AGGClient } from "../../clients";

import type { Template, TemplateCollection } from "./template.types";

interface TemplateContextValue {
    templates: Template[];
    partnerTemplates: Template[];
    collections: TemplateCollection[];
    categories: TemplateCategory[];
}

export type SortLookup = {
    [templateUrlSlug: string]: number;
};

type SortProps = {
    [K in keyof TemplateContextValue as `${K}SortBy`]?: "name" | SortLookup;
};

type TemplateProviderProps = PropsWithChildren<
    Partial<TemplateContextValue> & SortProps
>;

const DEFAULT_TEMPLATE_CONTEXT_VALUE: TemplateContextValue = {
    templates: [],
    partnerTemplates: [],
    collections: [],
    categories: []
};

const TemplateContext = createContext<TemplateContextValue>(
    DEFAULT_TEMPLATE_CONTEXT_VALUE
);

function isTemplateContextValue(
    partial: Partial<TemplateContextValue>
): partial is TemplateContextValue {
    let key: keyof TemplateContextValue;
    for (key in DEFAULT_TEMPLATE_CONTEXT_VALUE) {
        const defaultValue = DEFAULT_TEMPLATE_CONTEXT_VALUE[key];
        const value = partial[key];
        if (
            typeof defaultValue !== typeof value ||
            Array.isArray(defaultValue) !== Array.isArray(value)
        ) {
            return false;
        }
    }
    return true;
}

type Pending = { status: "pending"; promise: Promise<void> };
type Resolved = { status: "resolved"; value: TemplateContextValue };
type Rejected = { status: "rejected"; error: Error };
type QueryState = Pending | Resolved | Rejected;

function createQueryState(): QueryState {
    const client = new AGGClient({
        gatewayUrl:
            process.env["NODE_ENV"] === "production"
                ? "https://www.atlassian.com/gateway/api/graphql"
                : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
    });

    const { route, locale } = getCurrentRoute();

    const state: QueryState = {
        status: "pending",
        promise: getTemplateData(route, locale, client)
            .then((result) => {
                state.status = "resolved";
                (state as Resolved).value = result;
            })
            .catch((err) => {
                state.status = "rejected";
                (state as Rejected).error = err;
            })
    };

    return state;
}

function sortBy<T extends TemplateContextValue[keyof TemplateContextValue]>(
    items: T,
    sortBy?: "name" | SortLookup
): T {
    if (!sortBy) {
        return [...items] as T;
    }
    if (sortBy === "name") {
        return [...items].sort((a, b) =>
            a.name && b.name ? a.name.localeCompare(b.name) : a.name ? -1 : 1
        ) as T;
    }

    return [...items].sort((a, b) => {
        const aRank = a.urlSlug ? sortBy[a.urlSlug] : undefined;
        const bRank = b.urlSlug ? sortBy[b.urlSlug] : undefined;
        // Sort by rank 1 is higher than 2
        // if aRank is undefined, it should be sorted lower than bRank
        if (aRank === undefined) {
            return bRank === undefined ? 0 : 1;
        }
        if (bRank === undefined) {
            return -1;
        }
        return aRank - bRank;
    }) as T;
}

/**
 * Creates a context value for `TemplateContext` from partial data.
 * If any of the required context is missing, this function will
 * log a warning and replace the missing context with defaults.
 *
 * NOTE: It does not throw because doing so could cause SSR to fail.
 * The assumption is that the missing context will be resolved by
 * a client-side query (see `toTemplateContextValueDeferred`).
 */
function toTemplateContextValue(
    props: Partial<TemplateContextValue>,
    sortProps: SortProps
): TemplateContextValue {
    const result: Partial<TemplateContextValue> = {};

    let key: keyof TemplateContextValue;
    for (key in DEFAULT_TEMPLATE_CONTEXT_VALUE) {
        const value = props[key];
        if (!value) {
            console.warn(`TemplateProvider is missing required prop: ${key}`);
            // @ts-ignore
            result[key] = DEFAULT_TEMPLATE_CONTEXT_VALUE[key];
        } else {
            const sortProp = sortProps[`${key}SortBy`];
            if (sortProp) {
                // @ts-ignore
                result[key] = sortBy(value, sortProp);
            } else {
                // @ts-ignore
                result[key] = value;
            }
        }
    }

    return result as TemplateContextValue;
}

/**
 * Like `toTemplateContextValue`, but creates getters for the missing properties
 * that will throw a promise until the query for data resolves. The getters
 * will then be replaced with the resolved values.
 *
 * NOTE: This effectively implements a fallback behavior for any context that was
 * missing during SSR, or if the consuming page is being served from static fallback.
 */
function toTemplateContextValueDeferred(
    props: Partial<TemplateContextValue>,
    sortProps: SortProps
): TemplateContextValue {
    let state: QueryState;

    /**
     * Creates a getter that will throw a promise until the query for
     * `templates` data resolves. The getter will then be replaced with
     * the resolved value.
     */
    function deferred(
        obj: Partial<TemplateContextValue>,
        name: keyof TemplateContextValue
    ) {
        return () => {
            state ??= createQueryState();
            switch (state.status) {
                case "pending":
                    throw state.promise;
                case "rejected":
                    throw state.error;
                case "resolved":
                    // Replace the getter with the resolved value, so
                    // that future calls to this property will no longer
                    // see the getter on this object.
                    const value = sortBy(
                        state.value[name],
                        sortProps[`${name}SortBy`]
                    );
                    Object.defineProperty(obj, name, {
                        writable: false,
                        enumerable: true,
                        value
                    });
                    return value;
            }
        };
    }

    const value = { ...props };

    let key: keyof TemplateContextValue;
    for (key in DEFAULT_TEMPLATE_CONTEXT_VALUE) {
        if (!value[key]) {
            Object.defineProperty(value, key, {
                enumerable: true,
                get: deferred(value, key)
            });
        }
    }

    return value as TemplateContextValue;
}

/**
 * Creates a context value for `TemplateContext` from partial data.
 *
 * On the server, this function will replace any missing context
 * with defaults. On the client, this function will replace
 * any missing context with getters that will throw a promise
 * until the the data load resolves.
 *
 * See `toTemplateContextValue` and `toTemplateContextValueDeferred` for more.
 */
function useTemplatesContextValue({
    templates,
    partnerTemplates,
    collections,
    categories,
    templatesSortBy,
    partnerTemplatesSortBy = templatesSortBy,
    collectionsSortBy,
    categoriesSortBy = CategoryOrder
}: Partial<TemplateContextValue> & SortProps): TemplateContextValue {
    const contextProps = useMemo<Partial<TemplateContextValue>>(
        () => ({
            templates,
            partnerTemplates,
            collections,
            categories
        }),
        [templates, partnerTemplates, collections, categories]
    );

    const sortProps = useMemo<SortProps>(
        () => ({
            templatesSortBy,
            partnerTemplatesSortBy,
            collectionsSortBy,
            categoriesSortBy
        }),
        [
            templatesSortBy,
            partnerTemplatesSortBy,
            collectionsSortBy,
            categoriesSortBy
        ]
    );

    const [{ contextValue, isPartialValue }, setContextValue] = useState(
        () => ({
            contextValue: toTemplateContextValue(contextProps, sortProps),
            isPartialValue: !isTemplateContextValue(contextProps)
        })
    );

    useEffect(() => {
        if (isPartialValue) {
            setContextValue({
                contextValue: toTemplateContextValueDeferred(
                    contextProps,
                    sortProps
                ),
                isPartialValue: false
            });
        }
    }, [contextProps, sortProps, isPartialValue]);

    return contextValue;
}

export const TemplateProvider = ({
    templates,
    partnerTemplates,
    collections,
    categories,
    templatesSortBy,
    collectionsSortBy,
    categoriesSortBy,
    children
}: TemplateProviderProps) => {
    const value = useTemplatesContextValue({
        templates,
        partnerTemplates,
        collections,
        categories,
        templatesSortBy,
        collectionsSortBy,
        categoriesSortBy
    });
    return (
        <TemplateContext.Provider value={value}>
            {children}
        </TemplateContext.Provider>
    );
};

/**
 * Custom hook to return the Template content.
 *
 * Components that use this hook should be wrapped in a `Suspense` boundary,
 * as this hook will throw a promise and query for data if the content
 * in the `TemplatesProvider` is nullish.
 */
export const useTemplates = (): TemplateContextValue["templates"] => {
    const context = useContext(TemplateContext);
    if (!context) {
        throw new Error(
            "calls to useTemplates must be wrapped in <TemplateProvider>"
        );
    }

    return context.templates;
};

/**
 * Custom hook to return the Template collections.
 *
 * Components that use this hook should be wrapped in a `Suspense` boundary,
 * as this hook will throw a promise and query for data if the content
 * in the `TemplatesProvider` is nullish.
 */
export const useCollections = (): TemplateContextValue["collections"] => {
    const context = useContext(TemplateContext);
    if (!context) {
        throw new Error(
            "calls to useCollections must be wrapped in <TemplateProvider>"
        );
    }

    return context.collections;
};

/**
 * Custom hook to return the Template categories.
 *
 * Components that use this hook should be wrapped in a `Suspense` boundary,
 * as this hook will throw a promise and query for data if the content
 * in the `TemplatesProvider` is nullish.
 */
export const useCategories = (): TemplateContextValue["categories"] => {
    const context = useContext(TemplateContext);
    if (!context) {
        throw new Error(
            "calls to useCategories must be wrapped in <TemplateProvider>"
        );
    }

    return context.categories;
};

export const usePartnerTemplates =
    (): TemplateContextValue["partnerTemplates"] => {
        const context = useContext(TemplateContext);
        if (!context) {
            throw new Error(
                "calls to usePartnerTemplates must be wrapped in <TemplateProvider>"
            );
        }

        return context.partnerTemplates;
    };
